import { Button } from '@/global/components/Button'
import ButtonBase from '@/global/components/Button/ButtonBase'
import IconButton from '@/global/components/Button/IconButton'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import Menu from '@/global/components/Menu'
import { ArrowAccordion } from '@/global/components/ui/Accordion'
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/global/components/ui/command'
import { Label } from '@/global/components/ui/label'
import { Sheet, SheetContent, SheetTitle } from '@/global/components/ui/sheet'
import { cn, isMac } from '@/lib/utils'
import { insensitive } from '@acme/common'
import { Link as GlobalLink } from 'global/components/Link'
import { ExternalLinkIcon, MenuIcon, SearchIcon } from 'lucide-react'
import {
    Fragment,
    type KeyboardEvent as ReactKeyboardEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom'
import type { Menu as TMenu } from 'routes/types'

interface IProps {
    itens: TMenu
}

type Subitem = TMenu[0]['subitens'][0]

const valorDoItem = (item: TMenu[0], subitem: Subitem) =>
    [
        insensitive(item.label),
        insensitive(subitem.label),
        item.label,
        subitem.label,
    ].join(' ')

const BUTTON_STYLES = [
    'dark:text-foreground text-background',
    'dark:hover:text-foreground hover:text-background',
    'dark:hover:bg-accent hover:bg-background/10',
].join(' ')

function CommandSearch({ itens }: { itens: TMenu }) {
    const [open, setOpen] = useState(false)
    const [selecionado, setSelecionado] = useState('')
    const navigate = useNavigate()

    const subitensPorValor = useMemo(() => {
        const mapa = new Map<string, Subitem>()
        for (const item of itens ?? [])
            for (const subitem of item.subitens)
                mapa.set(valorDoItem(item, subitem).toLowerCase(), subitem)
        return mapa
    }, [itens])

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                setOpen(prev => !prev)
            }
        }
        document.addEventListener('keydown', down)
        return () => document.removeEventListener('keydown', down)
    }, [])

    const handleSelect = (redirectTo: string, newTab?: boolean) => {
        setOpen(false)
        if (newTab) {
            window.open(redirectTo, '_blank', 'noopener,noreferrer')
        } else {
            navigate(redirectTo)
        }
    }

    const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'ArrowRight') return

        const subitem = subitensPorValor.get(selecionado.toLowerCase())
        if (!subitem) return

        e.preventDefault()
        handleSelect(subitem.redirectTo, true)
    }

    return (
        <>
            <Button
                variant="text"
                onClick={() => setOpen(true)}
                className={cn(BUTTON_STYLES, 'ml-auto gap-2 px-2')}
                title="Buscar (Ctrl+K)"
            >
                <SearchIcon size={16} />
                <span>Buscar</span>
                <kbd className="hidden xl:inline pointer-events-none text-xs opacity-70 border rounded px-1">
                    {isMac() ? '⌘K' : 'Ctrl+K'}
                </kbd>
            </Button>

            <CommandDialog
                open={open}
                onOpenChange={setOpen}
                value={selecionado}
                onValueChange={setSelecionado}
            >
                <CommandInput
                    placeholder="Buscar página..."
                    onKeyDown={handleKeyDown}
                />
                <CommandList>
                    <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
                    {itens?.map((item, index) => (
                        <Fragment key={item.label}>
                            <CommandGroup heading={item.label}>
                                {item.subitens.map(subitem => (
                                    <CommandItem
                                        key={subitem.redirectTo}
                                        value={valorDoItem(item, subitem)}
                                        onSelect={() =>
                                            handleSelect(
                                                subitem.redirectTo,
                                                subitem.newTab,
                                            )
                                        }
                                        className="flex items-center gap-2"
                                    >
                                        {subitem.icon && (
                                            <span className="size-4">
                                                {subitem.icon}
                                            </span>
                                        )}
                                        <span>{subitem.label}</span>
                                        {subitem.newTab && (
                                            <ExternalLinkIcon className="ml-auto opacity-40 size-3" />
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                            {index < itens.length - 1 && (
                                <CommandSeparator key={`sep-${item.label}`} />
                            )}
                        </Fragment>
                    ))}
                </CommandList>
                <CommandHelp />
            </CommandDialog>
        </>
    )
}

function CommandHelp() {
    const isMacPlatform = isMac()

    return (
        <div
            className={cn(
                'inline-flex items-center',
                'w-full justify-evenly border-t',
                'py-2 *:gap-y-1 select-none',
                '*:flex *:flex-col *: *:items-center',
            )}
        >
            <div>
                <kbd className="inline text-xs border rounded-sm px-1">
                    Enter
                </kbd>{' '}
                <Label>Abrir</Label>
            </div>
            <div className="h-full w-[1px] bg-border" />
            <div>
                <kbd className="inline text-xs border rounded-sm px-1">🠊</kbd>{' '}
                <Label>Abrir em nova página</Label>
            </div>
            <div className="h-full w-[1px] bg-border" />
            <div>
                <div className="inline-flex gap-x-1 items-center text-xs *:border *:rounded-sm *:px-1">
                    <kbd>{isMacPlatform ? '⌘' : 'Ctrl'}</kbd>
                    {'+'}
                    <kbd>K</kbd>
                </div>
                <Label>Fechar</Label>
            </div>
        </div>
    )
}

export function MenuResponsivo({ itens }: IProps) {
    const [menuAberto, setMenuAberto] = useState('')
    const [menuMobileAberto, setMenuMobileAberto] = useState('')
    const [isMenuVerticalAberto, setIsMenuVerticalAberto] = useState(false)

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const abrirMenu = (label: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        setMenuAberto(label)
    }

    const fecharMenuComDelay = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
            setMenuAberto('')
        }, 100)
    }

    const manterMenuAberto = (label: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        setMenuAberto(label)
    }

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    const fecharDrawer = () => {
        setMenuMobileAberto('')
        setIsMenuVerticalAberto(false)
    }

    return [
        <div key="menu-desktop" className="hidden xl:flex w-full items-center">
            {itens?.map(item => (
                <Menu.Root
                    key={item.label}
                    id={'botao_' + item.label}
                    position="left"
                    modal={false}
                    open={menuAberto === item.label}
                    onOpenChange={aberto =>
                        setMenuAberto(aberto ? item.label : '')
                    }
                    onMouseEnter={() => abrirMenu(item.label)}
                    onMouseLeave={fecharMenuComDelay}
                    arrow
                >
                    <Menu.Trigger>
                        <Button
                            id={'botao_' + item.label + '-trigger'}
                            variant="text"
                            className={BUTTON_STYLES}
                            onClick={() => abrirMenu(item.label)}
                        >
                            {item.label}
                        </Button>
                    </Menu.Trigger>
                    <Menu.List
                        className="min-w-(--radix-dropdown-menu-trigger-width)"
                        onMouseEnter={() => manterMenuAberto(item.label)}
                        onMouseLeave={fecharMenuComDelay}
                    >
                        {item.subitens.map(subitem => (
                            <LinkButton key={subitem.label} {...subitem} />
                        ))}
                    </Menu.List>
                </Menu.Root>
            ))}
            <CommandSearch itens={itens} />
        </div>,
        <div key="menu-mobile" className="flex xl:hidden w-full">
            <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                className="m-4"
                onClick={() => setIsMenuVerticalAberto(true)}
                id="botao_menuVertical"
            >
                <MenuIcon />
            </IconButton>
            <Sheet
                open={isMenuVerticalAberto}
                onOpenChange={open => !open && fecharDrawer()}
            >
                <SheetContent
                    showCloseButton={false}
                    side="left"
                    aria-describedby={undefined}
                    className="w-[clamp(300px,25%,80vw)] gap-0 sm:max-w-none"
                >
                    <SheetTitle className="sr-only">Menu</SheetTitle>
                    <ButtonBase
                        type="button"
                        variant="ghost"
                        onClick={() => fecharDrawer()}
                        className={cn(
                            'flex w-full justify-start items-center',
                            'gap-4 px-4 pt-8 pb-6 text-left text-foreground',
                            'hover:bg-primary/10',
                        )}
                    >
                        <MenuIcon className="size-8" />
                        <span className="text-xl">Menu</span>
                    </ButtonBase>
                    <HorizontalDivider />
                    <ArrowAccordion.Root
                        type="single"
                        collapsible
                        value={menuMobileAberto}
                        onValueChange={setMenuMobileAberto}
                        className={cn(
                            'flex flex-1 flex-col gap-1 px-1',
                            'overflow-y-auto [scrollbar-gutter:stable]',
                        )}
                    >
                        {itens?.map(item => (
                            <ArrowAccordion.Item
                                value={item.label}
                                key={item.label}
                                id={'botao_' + item.label + '_mobile'}
                                className="shadow-sm"
                            >
                                <ArrowAccordion.Trigger
                                    id={
                                        'botao_' +
                                        item.label +
                                        '_mobile-trigger'
                                    }
                                >
                                    {item.label}
                                </ArrowAccordion.Trigger>
                                <ArrowAccordion.Content className="flex flex-col p-0 *:pl-6">
                                    {item.subitens.map(value => (
                                        <LinkButton
                                            key={value.label}
                                            {...value}
                                            onClick={() => fecharDrawer()}
                                            mobile
                                        />
                                    ))}
                                </ArrowAccordion.Content>
                            </ArrowAccordion.Item>
                        ))}
                    </ArrowAccordion.Root>
                </SheetContent>
            </Sheet>
        </div>,
    ]
}

function LinkButton({
    onClick,
    mobile,
    ...props
}: TMenu[0]['subitens'][0] & {
    onClick?: () => any
    mobile?: boolean
}) {
    if (mobile)
        return (
            <GlobalLink
                id={'sub-botao_' + props.label + (mobile ? '_mobile' : '')}
                onClick={onClick}
                href={props.redirectTo}
                className="flex w-full items-center gap-4 p-3 no-underline text-foreground cursor-pointer transition-colors hover:bg-primary/10"
                target={props.newTab ? '_blank' : undefined}
            >
                {props.icon}
                <span className="text-base">{props.label}</span>
                {props.newTab && (
                    <div
                        title="Abre em uma nova aba"
                        className="ml-4 flex items-center"
                    >
                        <ExternalLinkIcon className="opacity-50 size-4" />
                    </div>
                )}
            </GlobalLink>
        )

    return (
        <ReactRouterLink
            id={'sub-botao_' + props.label + (mobile ? '_mobile' : '')}
            to={props.redirectTo}
            target={props.newTab ? '_blank' : undefined}
            rel={props.newTab ? 'noopener noreferrer' : undefined}
        >
            <Menu.Item onClick={onClick}>
                <span className="text-base inline-flex items-center justify-between">
                    {props.label}
                    {props.newTab && (
                        <div
                            title="Abre em uma nova aba"
                            className="ml-4 flex items-center"
                        >
                            <ExternalLinkIcon className="opacity-50 size-4" />
                        </div>
                    )}
                </span>
            </Menu.Item>
        </ReactRouterLink>
    )
}
